'use client';
// src/components/catalog/AddOnsFilters.tsx — ідентичний MoreLikeFilters, лише basePath /browse/addons/.
import { useCallback, useState, useTransition, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useRouter, useSearchParams } from 'next/navigation';
import { useT } from '@/hooks/useT';
import { usePriceConvert } from '@/lib/PriceContext';
import { ChevronDown, LayoutGrid, Search, X, SlidersHorizontal, Zap, Shield } from 'lucide-react';

import { GENRES } from './GenreFilter';
import { OfferFilter, readOfferParams } from './OfferFilter';
import { LanguageFilter, LANG_LIST, langName, ALL_LANG_TYPES, type LangType } from './LanguageFilter';

const PRICE_MAX = 100;

const PLAT_KEYS  = ['all', 'xsx', 'xone', 'pc', 'xcloud', 'handheld'] as const;
const PLAT_LABEL: Record<string, string> = {
  all: 'Всі', xsx: 'Series X|S', xone: 'Xbox One', pc: 'PC', xcloud: 'xCloud', handheld: 'Handheld',
};
const PLAT_CHIP: Record<string, string> = {
  all: 'on', xsx: 'on-xsx', xone: 'on-xone', pc: 'on-pc', xcloud: 'on-xcloud', handheld: 'on-handheld',
};

// -- StarRating --------------------------------------------------------------
function StarRating({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  const [hover, setHover] = useState(0);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      {[1, 2, 3, 4, 5].map(s => {
        const active = s <= (hover || value);
        return (
          <button key={s}
            onMouseEnter={() => setHover(s)} onMouseLeave={() => setHover(0)}
            onClick={() => onChange(s === value ? 0 : s)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '1px', color: active ? '#f59e0b' : 'var(--v4-fg-4)', fontSize: 16, lineHeight: 1, transform: hover === s ? 'scale(1.25)' : 'scale(1)', transition: 'transform .15s, color .15s' }}
            aria-label={`${s} зірки`}>★</button>
        );
      })}
      {value > 0 && (
        <button onClick={() => onChange(0)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--v4-fg-3)', padding: '1px', marginLeft: 2, display: 'flex', alignItems: 'center' }} aria-label="Скинути рейтинг">
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M1 1l8 8M9 1L1 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
        </button>
      )}
    </div>
  );
}

// -- YearDropdown ------------------------------------------------------------
const YEARS = [2026, 2025, 2024, 2023, 2022, 2021, 2020, 2019, 2018, 2017, 2016, 2015, 2010, 2005];

function YearDropdown({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [pos,  setPos]  = useState<{ top: number; left: number } | null>(null);
  const btnRef  = useRef<HTMLButtonElement>(null);
  const dropRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (!dropRef.current?.contains(e.target as Node) && !btnRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onScroll = () => setOpen(false);
    document.addEventListener('mousedown', close);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => { document.removeEventListener('mousedown', close); window.removeEventListener('scroll', onScroll); };
  }, [open]);

  function openDrop() {
    if (btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setPos({ top: r.bottom + 4, left: r.left });
    }
    setOpen(true);
  }

  const dropdown = open && pos ? (
    <div ref={dropRef} style={{ position: 'fixed', top: pos.top, left: pos.left, background: 'color-mix(in srgb, var(--v4-bg-elev) 92%, transparent)', backdropFilter: 'blur(22px) saturate(140%)', WebkitBackdropFilter: 'blur(22px) saturate(140%)', border: '1px solid var(--v4-border-hover)', borderRadius: 10, boxShadow: '0 12px 40px rgba(0,0,0,.7)', zIndex: 9999, minWidth: 130, maxHeight: 280, overflowY: 'auto' }}>
      <button onClick={() => { onChange(0); setOpen(false); }}
        style={{ display: 'flex', alignItems: 'center', width: '100%', padding: '7px 12px', background: value === 0 ? 'color-mix(in srgb, var(--gs-green) 12%, transparent)' : 'transparent', border: 'none', borderBottom: '1px solid var(--v4-border)', cursor: 'pointer', fontSize: 12, fontWeight: value === 0 ? 700 : 400, fontFamily: 'inherit', color: value === 0 ? 'var(--gs-green)' : 'var(--v4-fg-2)' }}>
        {t('platform_all')}
      </button>
      {YEARS.map((y, idx) => (
        <button key={y} onClick={() => { onChange(y); setOpen(false); }}
          style={{ display: 'flex', alignItems: 'center', width: '100%', padding: '7px 12px', background: y === value ? 'color-mix(in srgb, var(--gs-green) 12%, transparent)' : 'transparent', border: 'none', borderBottom: idx < YEARS.length - 1 ? '1px solid var(--v4-border)' : 'none', cursor: 'pointer', fontSize: 12, fontWeight: y === value ? 700 : 400, fontFamily: 'inherit', color: y === value ? 'var(--gs-green)' : 'var(--v4-fg-2)' }}>
          {y}+
        </button>
      ))}
    </div>
  ) : null;

  return (
    <div style={{ flexShrink: 0 }}>
      <button ref={btnRef} onClick={() => open ? setOpen(false) : openDrop()}
        className={`va-chip${value > 0 ? ' on' : ''}`}
        style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer' }}>
        {value > 0 ? `${value}+` : t('filter_year')}
        <ChevronDown size={10} style={{ opacity: .55, transition: 'transform .2s', transform: open ? 'rotate(180deg)' : 'none' }} />
      </button>
      {typeof document !== 'undefined' && dropdown && createPortal(dropdown, document.body)}
    </div>
  );
}

// -- PriceRangeSlider --------------------------------------------------------
function PriceRangeSlider({ priceMin, priceMax, onMinChange, onMaxChange }: {
  priceMin: number; priceMax: number;
  onMinChange: (v: number) => void; onMaxChange: (v: number) => void;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
      <span style={{ fontSize: 11, color: 'var(--v4-fg-3)', whiteSpace: 'nowrap', minWidth: 80, textAlign: 'right' }}>
        ${priceMin} — {priceMax >= PRICE_MAX ? '∞' : `$${priceMax}`}
      </span>
      <div className="price-range-wrap">
        <div className="price-range-track">
          <div className="price-range-fill" style={{ left: `${(priceMin / PRICE_MAX) * 100}%`, right: `${100 - (priceMax / PRICE_MAX) * 100}%` }} />
        </div>
        <input type="range" min={0} max={PRICE_MAX} step={1} value={priceMin} className="price-range-input"
          style={{ zIndex: priceMin > PRICE_MAX * 0.75 ? 5 : 3 }}
          onChange={e => onMinChange(Math.min(+e.target.value, priceMax - 1))} />
        <input type="range" min={0} max={PRICE_MAX} step={1} value={priceMax} className="price-range-input"
          style={{ zIndex: priceMax < PRICE_MAX * 0.25 ? 5 : 3 }}
          onChange={e => onMaxChange(Math.max(+e.target.value, priceMin + 1))} />
      </div>
    </div>
  );
}

// -- Main component ----------------------------------------------------------
export function AddOnsFilters({ productId }: { productId: string }) {
  const sp = useSearchParams();
  const router = useRouter();
  const t = useT();
  const { lang: siteLang } = usePriceConvert();
  const [, startTransition] = useTransition();
  const [genreRowOpen, setGenreRowOpen] = useState(false);

  // -- Mobile bottom-sheet state --
  const [sheetOpen, setSheetOpen] = useState(false);
  const [openSec, setOpenSec] = useState<Set<string>>(() => new Set(['sort']));
  const toggleSec = (id: string) => setOpenSec(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const Sec = (id: string, label: string, badge: number, content: React.ReactNode) => (
    <div className={`fbar-acc${openSec.has(id) ? ' open' : ''}`}>
      <button className="fbar-acc-head" onClick={() => toggleSec(id)}>
        <span className="fbar-acc-title">{label}{badge > 0 && <span className="fbar-acc-badge">{badge}</span>}</span>
        <ChevronDown size={18} className="fbar-acc-chev" />
      </button>
      <div className="fbar-acc-body"><div className="fbar-acc-inner">{content}</div></div>
    </div>
  );

  const sort      = sp.get('sort') ?? 'popular';
  const plat      = sp.get('plat') ?? 'all';
  const sale      = sp.get('sale') === '1';
  const genre     = sp.get('genre') ?? '';
  const minRating = Number(sp.get('minRating') ?? 0);
  const minYear   = Number(sp.get('minYear') ?? 0);
  const minPrice  = Number(sp.get('minPrice') ?? 0);
  const maxPrice  = Number(sp.get('maxPrice') ?? PRICE_MAX);
  const developer = sp.get('developer') ?? '';
  const optOnly   = sp.get('opt') === '1';
  const goldOnly  = sp.get('gold') === '1';
  const langFilter = (sp.get('lang') ?? '').split(',').filter(Boolean);
  const rawLangTypes = (sp.get('langt') ?? '').split(',').filter(Boolean) as LangType[];
  const langTypes: LangType[] = rawLangTypes.length ? rawLangTypes : ALL_LANG_TYPES;
  const { offerType, region } = readOfferParams(new URLSearchParams(sp.toString()));

  const updateLang = useCallback((langs: string[], types: LangType[]) => {
    update({
      lang:  langs.length ? langs.join(',') : null,
      langt: langs.length && types.length < 3 ? types.join(',') : null,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sp]);

  const [localPriceMin, setLocalPriceMin] = useState(minPrice);
  const [localPriceMax, setLocalPriceMax] = useState(maxPrice < PRICE_MAX ? maxPrice : PRICE_MAX);
  const priceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const hasFilters = sort !== 'popular' || plat !== 'all' ||
    sale || genre !== '' || minRating > 0 || minYear > 0 ||
    minPrice > 0 || maxPrice < PRICE_MAX || developer !== '' || langFilter.length > 0 ||
    optOnly || goldOnly;

  const update = useCallback((changes: Record<string, string | null>) => {
    const params = new URLSearchParams(sp.toString());
    params.delete('page');
    for (const [k, v] of Object.entries(changes)) {
      if (v === null || v === '' || v === '0' || v === 'all' || v === 'popular') params.delete(k);
      else params.set(k, v);
    }
    startTransition(() => {
      router.replace(`/browse/addons/${productId}?${params.toString()}`);
    });
  }, [sp, productId, router]);

  function reset() {
    setLocalPriceMin(0);
    setLocalPriceMax(PRICE_MAX);
    startTransition(() => { router.replace(`/browse/addons/${productId}`); });
  }

  function handlePriceMin(v: number) {
    setLocalPriceMin(v);
    if (priceTimer.current) clearTimeout(priceTimer.current);
    priceTimer.current = setTimeout(() => update({ minPrice: v > 0 ? String(v) : null }), 400);
  }
  function handlePriceMax(v: number) {
    setLocalPriceMax(v);
    if (priceTimer.current) clearTimeout(priceTimer.current);
    priceTimer.current = setTimeout(() => update({ maxPrice: v < PRICE_MAX ? String(v) : null }), 400);
  }

  function toggleSort(a: string, b: string) {
    const next = sort === a ? b : sort === b ? 'popular' : a;
    update({ sort: next === 'popular' ? null : next });
  }

  // filter-bar class has position:sticky; top:0 — override top to 52px to stick below title row
  return (
    <>
    {/* -- Mobile filter trigger (bottom-sheet) -- */}
    <div className="fbar-mobile">
      <button className="fbar-m-btn" onClick={() => setSheetOpen(true)}>
        <SlidersHorizontal size={15} /> {t('m_filters')}
        {hasFilters && <span className="fbar-m-badge">•</span>}
      </button>
    </div>

    <div className="filter-bar" style={{ top: 116 }}>
      <div className="filter-bar-inner">

        {/* Row 1: Sort + Rating + Year + Price */}
        <div className="fbar-row">
          <span className="fbar-label">{t('filter_sort')}</span>
          <button className={`va-chip${sort === 'popular' ? ' on' : ''}`} onClick={() => update({ sort: null })}>{t('sort_popular')}</button>
          <button className={`va-chip${sort === 'new' ? ' on' : ''}`} onClick={() => update({ sort: 'new' })}>{t('sort_new')}</button>
          <button className={`va-chip${sort === 'rating_desc' ? ' on' : ''}`}
            onClick={() => update({ sort: sort === 'rating_desc' ? null : 'rating_desc' })}>{t('filter_rating')}</button>
          <button className={`va-chip${sort === 'price_asc' || sort === 'price_desc' ? ' on' : ''}`}
            onClick={() => toggleSort('price_asc', 'price_desc')}>
            {sort === 'price_asc' ? `${t('filter_price')} ↑` : sort === 'price_desc' ? `${t('filter_price')} ↓` : t('filter_price')}
          </button>
          <button className={`va-chip${sort === 'discount_desc' ? ' on' : ''}`}
            onClick={() => update({ sort: sort === 'discount_desc' ? null : 'discount_desc' })}>{t('sort_discount_desc')}</button>

          <div className="fbar-sep" />
          <span className="fbar-label">{t('filter_rating')}</span>
          <StarRating value={minRating} onChange={v => update({ minRating: v > 0 ? String(v) : null })} />
          {minRating > 0 && (
            <span style={{ fontSize: 11, fontWeight: 700, color: '#f59e0b', background: 'rgba(245,158,11,.12)', border: '1px solid rgba(245,158,11,.25)', borderRadius: 6, padding: '2px 7px', whiteSpace: 'nowrap', flexShrink: 0 }}>★{minRating}+</span>
          )}

          <div className="fbar-sep" />
          <YearDropdown value={minYear} onChange={v => update({ minYear: v > 0 ? String(v) : null })} />

          <div style={{ flex: 1 }} />
          <PriceRangeSlider priceMin={localPriceMin} priceMax={localPriceMax} onMinChange={handlePriceMin} onMaxChange={handlePriceMax} />
        </div>

        {/* Row 2: Platform + Delivery type + Region + Studio + Reset + Genres toggle */}
        <div className="fbar-row fbar-row-2">
          <span className="fbar-label">{t('filter_platform')}</span>
          {PLAT_KEYS.map(p => (
            <button key={p} className={plat === p ? `va-chip ${PLAT_CHIP[p]}` : 'va-chip'} onClick={() => update({ plat: p })}>
              {p === 'all' ? t('platform_all') : PLAT_LABEL[p]}
            </button>
          ))}

          <div className="fbar-sep" />
          <button className={`va-chip${optOnly ? ' on' : ''}`} onClick={() => update({ opt: optOnly ? null : '1' })}
            style={{ display: 'flex', alignItems: 'center', gap: 5 }}><Zap size={13} />{t('catalog_optimized')}</button>
          <button className={`va-chip${goldOnly ? ' on-gold' : ''}`} onClick={() => update({ gold: goldOnly ? null : '1' })}
            style={{ display: 'flex', alignItems: 'center', gap: 5 }}><Shield size={13} />{t('filter_gold')}</button>

          <div className="fbar-sep" />
          {/* Тип видачі + регіон (?offer=&region=) — як на каталозі/знижках */}
          <OfferFilter basePath={`/browse/addons/${productId}`} />

          <div className="fbar-sep" />
          <LanguageFilter langs={langFilter} types={langTypes} onChange={updateLang} />

          <div className="fbar-sep" />
          <div className="fbar-studio-wrap">
            <Search size={12} className="fbar-studio-icon" />
            <input className="fbar-studio-input" placeholder={t('pdp_tech_developer')} value={developer}
              onChange={e => update({ developer: e.target.value.trim() || null })} />
            {developer && (
              <button className="fbar-studio-clear" onClick={() => update({ developer: null })} aria-label="Очистити">
                <X size={11} />
              </button>
            )}
          </div>

          <div style={{ flex: 1, minWidth: 8 }} />
          {hasFilters && <button className="fbar-reset-btn" onClick={reset}>{t('catalog_reset_active')}</button>}
          <button
            className={`va-chip${genreRowOpen || genre !== '' ? ' on' : ''}`}
            onClick={() => setGenreRowOpen(o => !o)}
            style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}
          >
            {t('filter_genres')}
            <ChevronDown size={10} style={{ transition: 'transform .2s ease', transform: genreRowOpen ? 'rotate(180deg)' : 'rotate(0deg)' }} />
          </button>
        </div>

        {/* Expandable genre row — same as catalog */}
        <div className={`fbar-genre-expand${genreRowOpen ? ' open' : ''}`}>
          <div className="fbar-row fbar-genre-row-inner">
            <button className={`va-chip${genre === '' ? ' on' : ''}`} onClick={() => update({ genre: null })}
              style={{ display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0 }}>
              <LayoutGrid size={11} strokeWidth={2} />
              {t('filter_all_genres')}
            </button>
            {GENRES.map(({ key, i18nKey, Icon }) => (
              <button key={key} className={`va-chip${genre === key ? ' on' : ''}`}
                onClick={() => update({ genre: genre === key ? null : key })}
                style={{ display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0 }}>
                <Icon size={11} strokeWidth={2} />
                {t(i18nKey as never)}
              </button>
            ))}
          </div>
        </div>

      </div>
    </div>

    {/* -- Mobile filter bottom-sheet -- */}
    <div className={`fbar-scrim${sheetOpen ? ' open' : ''}`} onClick={() => setSheetOpen(false)} />
    <div className={`fbar-sheet${sheetOpen ? ' open' : ''}`} role="dialog" aria-modal="true" aria-label={t('m_filters')}>
      <div className="fbar-sheet-grip" />
      <div className="fbar-sheet-head">
        <span className="fbar-sheet-title">{t('m_filters')}</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {hasFilters && <button className="fbar-sheet-reset" onClick={reset}>{t('m_reset')}</button>}
          <button className="fbar-sheet-x" aria-label="✕" onClick={() => setSheetOpen(false)}><X size={16} /></button>
        </div>
      </div>
      <div className="fbar-sheet-body">
        <div className="fbar-quick fbar-group-chips">
          <button className={`va-chip${optOnly ? ' on' : ''}`} onClick={() => update({ opt: optOnly ? null : '1' })} style={{ display: 'flex', alignItems: 'center', gap: 5 }}><Zap size={13} />{t('catalog_optimized')}</button>
          <button className={`va-chip${goldOnly ? ' on-gold' : ''}`} onClick={() => update({ gold: goldOnly ? null : '1' })} style={{ display: 'flex', alignItems: 'center', gap: 5 }}><Shield size={13} />{t('filter_gold')}</button>
        </div>
        {Sec('sort', t('m_sort'), sort !== 'popular' ? 1 : 0, (
          <div className="fbar-group-chips">
            <button className={`va-chip${sort === 'popular' ? ' on' : ''}`} onClick={() => update({ sort: null })}>{t('sort_popular')}</button>
            <button className={`va-chip${sort === 'new' ? ' on' : ''}`} onClick={() => update({ sort: 'new' })}>{t('sort_new')}</button>
            <button className={`va-chip${sort === 'rating_desc' ? ' on' : ''}`} onClick={() => update({ sort: sort === 'rating_desc' ? null : 'rating_desc' })}>{t('filter_rating')}</button>
            <button className={`va-chip${sort === 'price_asc' || sort === 'price_desc' ? ' on' : ''}`} onClick={() => toggleSort('price_asc', 'price_desc')}>
              {sort === 'price_asc' ? `${t('filter_price')} ↑` : sort === 'price_desc' ? `${t('filter_price')} ↓` : t('filter_price')}
            </button>
            <button className={`va-chip${sort === 'discount_desc' ? ' on' : ''}`} onClick={() => update({ sort: sort === 'discount_desc' ? null : 'discount_desc' })}>{t('sort_discount_desc')}</button>
          </div>
        ))}
        {Sec('platform', t('filter_platform'), plat !== 'all' ? 1 : 0, (
          <div className="fbar-group-chips">
            {PLAT_KEYS.map(p => (
              <button key={p} className={plat === p ? `va-chip ${PLAT_CHIP[p]}` : 'va-chip'} onClick={() => update({ plat: p })}>
                {p === 'all' ? t('platform_all') : PLAT_LABEL[p]}
              </button>
            ))}
          </div>
        ))}
        {Sec('genre', t('filter_genres'), genre !== '' ? 1 : 0, (
          <div className="fbar-group-chips">
            <button className={`va-chip${genre === '' ? ' on' : ''}`} onClick={() => update({ genre: null })} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <LayoutGrid size={12} /> {t('filter_all_genres')}
            </button>
            {GENRES.map(({ key, i18nKey, Icon }) => (
              <button key={key} className={`va-chip${genre === key ? ' on' : ''}`} onClick={() => update({ genre: genre === key ? null : key })} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <Icon size={12} /> {t(i18nKey as never)}
              </button>
            ))}
          </div>
        ))}
        {Sec('offer', `${t('m_offer')} / ${t('m_region')}`, (offerType !== 'key' ? 1 : 0) + (region !== 'any' ? 1 : 0), (
          <OfferFilter basePath={`/browse/addons/${productId}`} />
        ))}
        {Sec('lang', t('m_lang'), langFilter.length > 0 ? 1 : 0, (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div className="fbar-group-chips">
              {(['a', 's', 'i'] as LangType[]).map(tk => (
                <button key={tk} className={`va-chip${langTypes.includes(tk) ? ' on' : ''}`}
                  onClick={() => { const nt = langTypes.includes(tk) ? langTypes.filter(x => x !== tk) : [...langTypes, tk]; updateLang(langFilter, nt.length ? nt : ALL_LANG_TYPES); }}>
                  {tk === 'a' ? t('filter_lang_audio') : tk === 's' ? t('filter_lang_subs') : t('filter_lang_interface')}
                </button>
              ))}
            </div>
            <div className="fbar-group-chips">
              {LANG_LIST.map(l => (
                <button key={l.code} className={`va-chip${langFilter.includes(l.code) ? ' on' : ''}`}
                  onClick={() => { const nf = langFilter.includes(l.code) ? langFilter.filter(x => x !== l.code) : [...langFilter, l.code]; updateLang(nf, langTypes); }}
                  style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  {l.flag} <span suppressHydrationWarning>{langName(l.code, siteLang, l.label)}</span>
                </button>
              ))}
            </div>
          </div>
        ))}
        {Sec('dev', t('pdp_tech_developer'), developer ? 1 : 0, (
          <div className="fbar-studio-mobile">
            <Search size={15} />
            <input placeholder={`${t('pdp_tech_developer')}...`} value={developer} onChange={e => update({ developer: e.target.value.trim() || null })} autoComplete="off" />
          </div>
        ))}
        {Sec('rating', t('filter_rating'), minRating > 0 ? 1 : 0, (
          <div className="fbar-rating">
            {[1, 2, 3, 4, 5].map(sv => (
              <button key={sv} className={`va-star${sv <= minRating ? ' on' : ''}`} aria-label={`★${sv}`}
                onClick={() => update({ minRating: sv === minRating ? null : String(sv) })}>★</button>
            ))}
          </div>
        ))}
        {Sec('year', t('filter_year'), minYear > 0 ? 1 : 0, (
          <div className="fbar-group-chips">
            {YEARS.map(y => (
              <button key={y} className={`va-chip${minYear === y ? ' on' : ''}`} onClick={() => update({ minYear: minYear === y ? null : String(y) })}>{y}+</button>
            ))}
          </div>
        ))}
        {Sec('price', t('filter_price'), (minPrice > 0 || maxPrice < PRICE_MAX) ? 1 : 0, (
          <div className="fbar-price">
            <div className="fbar-price-vals">
              <span>${localPriceMin}</span>
              <span>{localPriceMax >= PRICE_MAX ? '∞' : `$${localPriceMax}`}</span>
            </div>
            <div className="price-range-wrap">
              <div className="price-range-track"><div className="price-range-fill" style={{ left: `${(localPriceMin / PRICE_MAX) * 100}%`, right: `${100 - (localPriceMax / PRICE_MAX) * 100}%` }} /></div>
              <input type="range" min={0} max={PRICE_MAX} step={1} value={localPriceMin} className="price-range-input" style={{ zIndex: 3 }} onChange={e => handlePriceMin(Math.min(+e.target.value, localPriceMax - 1))} />
              <input type="range" min={0} max={PRICE_MAX} step={1} value={localPriceMax} className="price-range-input" style={{ zIndex: 3 }} onChange={e => handlePriceMax(Math.max(+e.target.value, localPriceMin + 1))} />
            </div>
          </div>
        ))}
      </div>
      <div className="fbar-sheet-foot">
        <button className="fbar-sheet-apply" onClick={() => setSheetOpen(false)}>{t('m_show')}</button>
      </div>
    </div>
    </>
  );
}
